import {
  Asterisk,
  Delete,
  Divide,
  Equal,
  Minus,
  Plus,
} from "@tamagui/lucide-icons";
import { Parser } from "expr-eval";
import { computed, makeAutoObservable, reaction, transaction } from "mobx";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type PropsWithChildren, useEffect, useMemo, useState } from "react";
import { Platform } from "react-native";
import Animated, { useSharedValue, withTiming } from "react-native-reanimated";
import { styled, View, type ViewProps, XStack } from "tamagui";
import { getLanguage } from "../../../core/utils";
import { Body } from "../../../ui/Body";

const operators = ["*", "÷", "-", "+"] as const;
type Operators = (typeof operators)[number];

// Function to check if the key pressed is one of the specified characters
function isNumber(key: string): boolean {
  return "0123456789".includes(key);
}

// Utility function to check and handle valid operator
function isOperator(key: string): Operators | undefined {
  const operators = {
    "*": "*",
    "/": "÷", // We translate the division symbol to a more commonly used symbol in displays.
    "-": "-",
    "+": "+",
  } as const;

  return operators[key as keyof typeof operators];
}

function getDecimalSeparator(locale: string): string {
  const numberWithDecimalSeparator = 1.1;
  return Intl.NumberFormat(locale)
    .format(numberWithDecimalSeparator)
    .substring(1, 2);
}

// Function to format the calculator's display
function formatCalculatorDisplay(
  currentValue: string,
  decimalSeparator = ".",
): string {
  // Regular expression to match operators and ensure spaces around them
  let formattedValue = currentValue.replace(/\s*([+\-÷*])\s*/g, " $1 ");

  // Replace dot with the locale specific decimal separator if necessary
  if (decimalSeparator === ",") {
    formattedValue = formattedValue.replace(/\./g, decimalSeparator);
  } else if (decimalSeparator === ".") {
    formattedValue = formattedValue.replace(/,/g, decimalSeparator);
  }

  return formattedValue.trim();
}

type CalculatorStateOptions = { expanded?: boolean };

export class CalculatorState {
  currentValue: string;
  locale = getLanguage();
  decimalSeparator = getDecimalSeparator(this.locale);
  onChange?: (value: number | undefined, readable: string) => void;
  expanded: boolean;
  inputKey = "";
  onChangeReaction: () => void;
  hasChanged = false;

  constructor(
    startValue: number,
    protected options?: CalculatorStateOptions,
  ) {
    this.expanded = options?.expanded ?? true;
    this.currentValue = String(startValue);

    makeAutoObservable(this);

    this.onChangeReaction = reaction(
      () => this.currentValue,
      () => this.triggerChange(),
    );

    if (Platform.OS === "web") {
      window.addEventListener("keydown", this.handleKeyUp.bind(this));
    }
  }

  triggerChange() {
    const numberValue = this.parseExpression();

    this.onChange?.(numberValue, this.readable);
  }

  setInputKey(inputKey?: string) {
    this.inputKey = inputKey ?? "";
  }

  setExpanded(
    value: boolean,
    inputKey?: string,
    currentValue?: string,
    onChange?: (value: number | undefined, readable: string) => void,
  ) {
    if (inputKey === this.inputKey) {
      return;
    }

    this.evaluate();
    this.triggerChange();

    transaction(() => {
      this.hasChanged = false;
      this.currentValue = currentValue ?? "0";
      this.expanded = value;
      this.inputKey = inputKey ?? "";

      this.onChange = onChange;
    });
  }

  setValue(value: number) {
    this.currentValue = String(value);
  }

  handleKeyUp(event: KeyboardEvent): void {
    if (!this.expanded) {
      return;
    }

    const key = event.key;

    if (isNumber(key)) {
      this.addNumber(Number(key));
    } else if (key === "Backspace") {
      this.delete();
    } else if (key === "Enter") {
      this.evaluate();
    } else if (key === "," || key === ".") {
      this.addDecimalSeparator();
    } else {
      const operator = isOperator(key);
      if (operator) {
        this.addOperator(operator);
      }
    }
  }

  addNumber(value: number) {
    if (!this.hasChanged) {
      this.currentValue = String(value);
      this.hasChanged = true;
      return;
    }

    if (this.currentValue === "0") {
      this.currentValue = String(value);
      return;
    }

    this.currentValue += String(value);
  }

  addOperator(operator: Operators) {
    if (!this.currentValue || this.currentValue === "0") {
      return;
    }

    transaction(() => {
      this.trimOperator();

      this.evaluate();

      this.currentValue += operator;
    });
  }

  trimOperator() {
    const lastCharacter = this.currentValue.slice(-1);

    // biome-ignore lint/suspicious/noExplicitAny: Because
    if (operators.includes(lastCharacter as any)) {
      this.delete();
    }
  }

  addDecimalSeparator() {
    // Find the last operator to determine the start of the current number
    const lastOperatorIndex = Math.max(
      ...operators.map((operator) => this.currentValue.lastIndexOf(operator)),
    );

    // Extract the current number after the last operator
    const currentNumber = this.currentValue.substring(lastOperatorIndex + 1);

    // Check if the current number already contains a decimal separator
    if (!currentNumber.includes(".")) {
      this.currentValue += ".";
    }
  }

  delete() {
    if (this.currentValue.length <= 1 || this.currentValue === "0") {
      this.currentValue = "0";
      return;
    }

    this.currentValue = this.currentValue.slice(0, -1);
  }

  clear() {
    this.currentValue = "0";
  }

  @computed
  get readable() {
    return formatCalculatorDisplay(this.currentValue, this.decimalSeparator);
  }

  parseExpression() {
    try {
      const result = Parser.parse(
        this.currentValue.replaceAll("÷", "/"),
      ).evaluate();

      if (result === Number.POSITIVE_INFINITY) {
        throw new Error("Division by zero");
      }

      return result;
    } catch {
      return undefined;
    }
  }

  evaluate() {
    this.trimOperator();

    try {
      const result = this.parseExpression();

      if (typeof result === "number") {
        this.currentValue = String(result);
      }
    } catch (_e) {
      this.currentValue = "0";
    }
  }

  cleanup() {
    window.removeEventListener("keyup", this.handleKeyUp);
  }
}

const Row: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <View gap={1} flex={1} flexDirection="row">
      {children}
    </View>
  );
};

const PressableCell = styled(View, {
  backgroundColor: "$divider",
  flexBasis: 0,
  pressStyle: {
    opacity: 0.75,
    scaleX: 0.99,
    scaleY: 0.97,
  },
});

const Cell: React.FC<
  PropsWithChildren & ViewProps & { onPress: () => void }
> = ({ children, ...viewProps }) => {
  return (
    <PressableCell
      {...viewProps}
      flex={1}
      style={{ flexBasis: 0 }}
      alignItems="center"
      justifyContent="center"
    >
      {typeof children === "string" ? <Body>{children}</Body> : children}
    </PressableCell>
  );
};

const NumbericCell: React.FC<{ state: CalculatorState; children: string }> =
  observer(({ children: value, state }) => {
    return <Cell onPress={() => state.addNumber(Number(value))}>{value}</Cell>;
  });

const OperatorCell: React.FC<
  PropsWithChildren<{ state: CalculatorState; operator: Operators }>
> = observer(({ children, operator, state }) => {
  return (
    <Cell
      backgroundColor={"$primary"}
      flex={1}
      onPress={() => state.addOperator(operator)}
    >
      {children}
    </Cell>
  );
});

type CalculatorProps = {
  state: CalculatorState;
  initialValue?: number;
  expanded?: boolean;
  label?: string;
};

const Calculator: React.FC<{
  state: CalculatorState;
  initialValue?: number;
  label?: string;
}> = observer(({ state }) => {
  const calculatorHeight = 220;

  useEffect(() => {
    () => state.cleanup();
  }, [state]);

  const animatedCalculatorHeight = useSharedValue(
    state.expanded ? calculatorHeight : 0,
  );

  useEffect(() => {
    return reaction(
      () => state.expanded,
      (expanded) => {
        animatedCalculatorHeight.value = withTiming(
          expanded ? calculatorHeight : 0,
        );
      },
    );
  }, [animatedCalculatorHeight, state]);

  return (
    <View backgroundColor={"$background"}>
      <Animated.View
        style={{ height: animatedCalculatorHeight, overflow: "hidden" }}
      >
        <XStack
          height={calculatorHeight}
          flexDirection="row"
          gap={1}
          width="100%"
          userSelect="none"
        >
          <View flex={1} gap={1}>
            <Row>
              <NumbericCell state={state}>7</NumbericCell>
              <NumbericCell state={state}>8</NumbericCell>
              <NumbericCell state={state}>9</NumbericCell>
            </Row>

            <Row>
              <NumbericCell state={state}>4</NumbericCell>
              <NumbericCell state={state}>5</NumbericCell>
              <NumbericCell state={state}>6</NumbericCell>
            </Row>

            <Row>
              <NumbericCell state={state}>1</NumbericCell>
              <NumbericCell state={state}>2</NumbericCell>
              <NumbericCell state={state}>3</NumbericCell>
            </Row>

            <Row>
              <Cell onPress={() => state.addDecimalSeparator()}>
                {state.decimalSeparator}
              </Cell>
              <NumbericCell state={state}>0</NumbericCell>
              <Cell onPress={() => state.delete()}>
                <Delete />
              </Cell>
            </Row>
          </View>
          <View width={50} height={calculatorHeight} gap={1}>
            <OperatorCell state={state} operator="÷">
              <Divide />
            </OperatorCell>
            <OperatorCell state={state} operator="*">
              <Asterisk />
            </OperatorCell>
            <OperatorCell state={state} operator="-">
              <Minus />
            </OperatorCell>
            <OperatorCell state={state} operator="+">
              <Plus />
            </OperatorCell>
            <Cell backgroundColor={"$primary"} onPress={() => state.evaluate()}>
              <Equal />
            </Cell>
          </View>
        </XStack>
      </Animated.View>
    </View>
  );
});

export const useCalculator = (
  value: number,
  options?: CalculatorStateOptions,
) => {
  const [state] = useState<CalculatorState>(
    () => new CalculatorState(value, options),
  );

  return useMemo(() => {
    return {
      Calculator: (props: Omit<CalculatorProps, "state">) => {
        return <Calculator {...props} state={state} />;
      },
      state,
    };
  }, [state]);
};
